import { z } from 'zod'

// ─── Enums ───

export const NoteKindSchema = z.enum(['stream', 'note'])
export type NoteKind = z.infer<typeof NoteKindSchema>

export const SentimentSchema = z.enum(['bullish', 'bearish', 'neutral'])
export type Sentiment = z.infer<typeof SentimentSchema>

export const CategorySchema = z.enum([
  'thesis',
  'setup',
  'observation',
  'catalyst',
  'risk',
  'question',
])
export type Category = z.infer<typeof CategorySchema>

/** Fixed set of allowed tags for filtering and LLM enrichment. */
export const NOTE_TAGS = [
  'momentum',
  'breakout',
  'breakdown',
  'reversal',
  'squeeze',
  'gap',
  'support',
  'resistance',
  'trend',
  'earnings',
  'catalyst',
  'news',
  'scalp',
  'swing',
  'runner',
  'volume',
  'vwap',
  'premarket',
  'options',
  'sector',
  'macro',
  'technical',
  'fundamental',
] as const

export type NoteTag = (typeof NOTE_TAGS)[number]

export const MarketSessionSchema = z.enum(['premarket', 'market', 'afterhours', 'weekend'])
export type MarketSession = z.infer<typeof MarketSessionSchema>

// 'done' is the success state for stream notes after the async enrichment
// runner writes back. 'enriched' is the synthetic "no enrichment needed" state
// set at creation time for non-stream notes (kind='note').
// 'pending' is the initial state for stream notes; 'failed' is set on error.
export const EnrichmentStatusSchema = z.enum(['pending', 'enriched', 'done', 'failed'])
export type EnrichmentStatus = z.infer<typeof EnrichmentStatusSchema>

// ─── View Mode ───

export type ViewMode = { mode: 'stream' } | { mode: 'note'; noteId: string | null } // null = new unsaved note

// ─── Ticker Entry (from note_tickers table) ───

export const TickerEntrySchema = z.object({
  ticker: z.string(),
  sentiment: SentimentSchema.nullable(),
  confidence: z.number().nullable(),
})
export type TickerEntry = z.infer<typeof TickerEntrySchema>

// ─── Note ───

export const NoteSourceSchema = z.enum(['text', 'voice', 'auto'])
export type NoteSource = z.infer<typeof NoteSourceSchema>

export const NoteAuthorSchema = z.enum(['user', 'system'])
export type NoteAuthor = z.infer<typeof NoteAuthorSchema>

export const NoteSchema = z.object({
  note_id: z.string(),
  content: z.string(),
  kind: NoteKindSchema,
  title: z.string().nullable(),
  content_json: z.string().nullable(),
  tickers: z.array(z.string()).nullable(),
  sentiment: SentimentSchema.nullable(),
  category: CategorySchema.nullable(),
  tags: z.array(z.string()).nullable(),
  market_session: MarketSessionSchema.nullable(),
  linked_prediction_id: z.number().nullable(),
  parent_note_id: z.string().nullable(),
  enrichment_status: EnrichmentStatusSchema,
  created_at: z.string(),
  updated_at: z.string(),
  ticker_entries: z.array(TickerEntrySchema),
  source: NoteSourceSchema.default('text'),
  author: NoteAuthorSchema.default('user'),
})
export type Note = z.infer<typeof NoteSchema>

// ─── API Request Types ───

export interface CreateNoteRequest {
  content: string
  kind?: 'stream' | 'note'
  title?: string
  content_json?: string
  tickers?: string[]
  parent_note_id?: string
  source?: 'text' | 'voice' | 'auto'
  // Client-supplied UUID. The backend dedupes on it — repeated POSTs with
  // the same id return the existing row instead of creating a duplicate.
  note_id?: string
}

export interface UpdateNoteRequest {
  content?: string
  title?: string
  content_json?: string
  linked_prediction_id?: number | null
  source?: 'text' | 'voice' | 'auto'
}

export interface NoteListParams {
  kind?: 'stream' | 'note'
  ticker?: string
  date_from?: string
  date_to?: string
  category?: string
  sentiment?: string
  /** Free-text search across note title + content (case-insensitive substring). */
  q?: string
  limit?: number
  offset?: number
}

// ─── Ticker Group (sidebar) ───

export const TickerGroupSchema = z.object({
  ticker: z.string(),
  count: z.number(),
})
export type TickerGroup = z.infer<typeof TickerGroupSchema>

// ─── Panel State ───

const ViewModeSchema = z.union([
  z.object({ mode: z.literal('stream') }),
  z.object({ mode: z.literal('note'), noteId: z.string().nullable() }),
])

const NoteFiltersSchema = z.object({
  ticker: z.string().optional(),
  // Feed toolbar: 'prediction' = notes with a linked prediction, 'note' = without.
  itemType: z.enum(['prediction', 'note']).optional(),
  // Feed toolbar: restrict to notes whose linked prediction resolved this way.
  outcome: z.enum(['correct', 'incorrect']).optional(),
  tag: z.string().optional(),
})

export type NoteFilters = z.infer<typeof NoteFiltersSchema>

export const NotebookViewSchema = z.enum([
  'feed',
  'notes',
  'tickers',
  'performance',
  'planner',
  'review',
])
export type NotebookView = z.infer<typeof NotebookViewSchema>

export const NotebookPanelStateSchema = z
  .object({
    viewMode: ViewModeSchema.optional(),
    sidebarCollapsed: z.boolean().optional(),
    activeNoteId: z.string().nullable().optional(),
    notesListCollapsed: z.boolean().optional(),
    tickersExpanded: z.boolean().optional(),
    plannerExpanded: z.boolean().optional(),
    filters: NoteFiltersSchema.optional(),
    view: NotebookViewSchema.optional(),
    selectedDay: z.string().nullable().optional(),
    selectedTicker: z.string().nullable().optional(),
    selectedFolder: z.string().nullable().optional(),
    pinnedTickers: z.array(z.string()).optional(),
    // Trade Planner sub-state. `plannerStrategyId` undefined means "never
    // chosen" (the page auto-selects the first strategy); an explicit null
    // means the user cleared it and wants the create call to action.
    plannerStrategyId: z.string().nullable().optional(),
    // null = follow the latest run for the selected strategy.
    plannerRunDate: z.string().nullable().optional(),
    plannerTicker: z.string().nullable().optional(),
    plannerView: z.enum(['split', 'list']).optional(),
  })
  .passthrough()

export type NotebookPanelState = z.infer<typeof NotebookPanelStateSchema>

/** Trade Planner main-pane layout: split (list beside the plan card) or list. */
export type PlannerView = 'split' | 'list'
